import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpenIcon, CheckIcon, ClockIcon, LockIcon, PlayIcon } from "lucide-react";
import type { Course } from "../../types/learning";
import { BrutalButton } from "../ui/BrutalButton";
import { PairChips } from "../ui/LangChip";
import { courseTitle, nextLevel } from "../../utils/language";
import { currentLesson, lessonStatus, unitProgress } from "../../utils/course";
import { bookPath, lessonPath } from "../../utils/routes";
import { spring } from "../../utils/motion";
interface ResumeCardProps {
  course: Course;
}
export function ResumeCard({
  course
}: ResumeCardProps) {
  const reduce = useReducedMotion() ?? false;
  const {
    unit,
    lesson
  } = currentLesson(course);
  const pct = unitProgress(course);
  return <motion.section aria-labelledby="resume-title" initial={reduce ? false : {
    opacity: 0,
    y: 20,
    scale: 0.98
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={spring.heavy} className="tex-ink-dots relative overflow-hidden rounded-panel border-3 border-line bg-brand-tint p-6 text-ink shadow-brutal md:p-8" data-id="element-201" data-mp="2qvpk2tn7xiuy">
      <div className="flex flex-wrap items-center gap-3" data-id="element-202" data-mp="12k7drsko5gq9">
        <PairChips target={course.target} base={course.base} size="md" data-id="element-203" data-mp="12jpbofbbs8af" />
        <span className="pl-1 text-sm font-semibold text-ink-2" data-id="element-204" data-mp="17i0gucqodumb">
          {courseTitle(course)} · {course.level}
        </span>
      </div>

      <div className="mt-6 grid gap-8 md:grid-cols-[1fr_auto] md:items-end" data-id="element-205" data-mp="tpspx1m2oxvj">
        <div className="min-w-0" data-id="element-206" data-mp="yjx0rvyglq1w">
          <p className="text-sm font-semibold text-ink-2" data-id="element-207" data-mp="jthdmbmhg813">
            Unit {unit.number} · {unit.title} · Lesson {course.lesson} of {unit.lessons.length}
          </p>
          <h2 id="resume-title" className="mt-1 text-[34px] font-bold leading-[1.05] tracking-[-0.02em] md:text-5xl" data-id="element-208" data-mp="ostw6k6bmsxv">
            {lesson.title}
          </h2>
          <p className="mt-3 flex items-center gap-2 text-sm text-ink-2" data-id="element-209" data-mp="eumjidj2ex1o">
            <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-210" data-mp="2hgrvt0dm987q" /> About 6 minutes · 7 exercises
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4" data-id="element-211" data-mp="2cptd5u55ny1b">
            <BrutalButton to={lessonPath(course, lesson.id)} size="lg" data-id="element-212" data-mp="2coi9dubpmqa7">
              <PlayIcon className="h-5 w-5 fill-current" aria-hidden="true" data-id="element-213" data-mp="27rrsx5vztso9" /> Continue
            </BrutalButton>
            <Link to={bookPath(course)} className="focus-ring flex items-center gap-2 rounded-xl px-2 py-2 text-[15px] font-semibold text-ink underline decoration-ink/30 decoration-2 underline-offset-4 hover:decoration-brand" data-id="element-214" data-mp="22tymqcuowynz">
              <BookOpenIcon className="h-4 w-4" aria-hidden="true" data-id="element-215" data-mp="2qgomf7jkgh5f" /> Read chapter {unit.number}
            </Link>
          </div>
        </div>

        <div className="rounded-card bg-surface/70 p-4 md:w-64" data-id="element-216" data-mp="2mih7mi8nxpwv">
          <ol aria-label={`Unit ${unit.number} lessons`} className="flex items-center gap-1.5" data-id="element-217" data-mp="1f3ckx3ba4amf">
            {unit.lessons.map((l, i) => {
            const status = lessonStatus(course, unit.number, i);
            return <li key={l.id} title={l.title} className={`grid h-8 flex-1 place-items-center rounded-lg ${status === "done" ? "bg-mint text-good" : status === "current" ? "border-2 border-line/70 bg-accent text-night" : "bg-sunken text-ink-2"}`} data-id="element-218" data-mp="3mmhyzovnd2ds" data-mpi={i}>
                  {status === "done" ? <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} aria-label="Done" data-id="element-219" data-mp="eam2dxvpwmho" /> : status === "current" ? <PlayIcon className="h-3 w-3 fill-current" aria-label="Current" data-id="element-220" data-mp="n9pmn56mf7xr" /> : <LockIcon className="h-3 w-3 opacity-70" aria-label="Locked" data-id="element-221" data-mp="2k29ajru3f6ow" />}
                </li>;
          })}
          </ol>
          <div className="mt-4 flex items-baseline justify-between text-sm" data-id="element-222" data-mp="1tqe13u6xnf34">
            <span className="font-semibold" data-id="element-223" data-mp="1tsh1n85poqm3">Unit progress</span>
            <span className="font-bold" data-id="element-224" data-mp="1oq9o4e58h2hn">{pct}%</span>
          </div>
          <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-sunken" data-id="element-225" data-mp="23oafcuv62q5g">
            <motion.div initial={reduce ? false : {
            scaleX: 0
          }} animate={{
            scaleX: pct / 100
          }} transition={{
            ...spring.smooth,
            delay: 0.2
          }} className="h-full origin-left rounded-full bg-brand" data-id="element-226" data-mp="1yph9nom4g3fu" />
          </div>
          <p className="mt-2 text-xs text-ink-2" data-id="element-227" data-mp="15yp5o4yteahy">
            {course.progress}% of the way to {nextLevel(course.level)}
          </p>
        </div>
      </div>
    </motion.section>;
}